import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import uniq from "lodash/uniq";
import { FC, useEffect, useState } from "react";
import { ComboGroupedElement } from "shared/components/Combobox/type";
import CountIndicator from "shared/components/CountIndicator";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { COLOR_GREY_12 } from "shared/theme/colors";
import { ExpandMoreIcon } from "shared/theme/icons/ExpandMoreIcon";
import {
  StyledContainer,
  StyledCollapseHeader,
  StyledCollapseContent,
  StyledIconWrapper,
  StyledCheckbox,
  StyledCheckboxWrapper,
  StyledEmptyState,
} from "./styled";

type ComboContentProps = {
  groupedElements: ComboGroupedElement[];
  onSelectGroupedOptions: (values: string[]) => void;
  selectedValues: string[];
  isExpandedAll: boolean;
};

const ComboContent: FC<ComboContentProps> = ({
  groupedElements,
  onSelectGroupedOptions,
  selectedValues,
  isExpandedAll,
}) => {
  const [openSections, setOpenSections] = useState<string[]>([]);

  useEffect(() => {
    if (isExpandedAll) {
      setOpenSections(groupedElements.map(({ value }) => value));
    }
  }, [isExpandedAll, groupedElements]);

  const toggleSection = (value: string) => {
    setOpenSections((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  if (groupedElements.length === 0) {
    return <StyledEmptyState>No results found</StyledEmptyState>;
  }

  return (
    <StyledContainer>
      {groupedElements
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ value, name, options }) => {
          const isOpen = openSections.includes(value);
          const selectedCount = options?.filter(({ value }) =>
            selectedValues.includes(value),
          ).length;

          return (
            <div key={value}>
              <StyledCollapseHeader onClick={() => toggleSection(value)}>
                <FlexCenter gap={"0.5rem"}>
                  <Typography variant="bodySSemiBold" color={COLOR_GREY_12}>
                    {name}
                  </Typography>
                  {Boolean(selectedCount) && (
                    <CountIndicator count={selectedCount} isSmallIndicator />
                  )}
                </FlexCenter>
                <StyledIconWrapper isOpen={isOpen}>
                  <ExpandMoreIcon />
                </StyledIconWrapper>
              </StyledCollapseHeader>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <StyledCollapseContent>
                  {options.map(({ value, name }) => (
                    <StyledCheckboxWrapper
                      key={value}
                      onClick={() => {
                        const isSelected = selectedValues.includes(value);
                        const newSelectedValues = isSelected
                          ? selectedValues.filter((item) => item !== value)
                          : [...selectedValues, value];

                        onSelectGroupedOptions(uniq(newSelectedValues));
                      }}
                    >
                      <StyledCheckbox
                        checked={uniq(selectedValues).includes(value)}
                        size="small"
                      />
                      <Typography variant="bodySMedium">{name}</Typography>
                    </StyledCheckboxWrapper>
                  ))}
                </StyledCollapseContent>
              </Collapse>
            </div>
          );
        })}
    </StyledContainer>
  );
};

export default ComboContent;
